import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Card — a superfície elevada da aplicação, e a única.
 *
 * Hard rule 5: tudo o que é "um bloco branco com borda" nasce daqui — painel de
 * tabela, cartão de etapa, painel de detalhe. Sem isso as mesmas seis classes
 * reaparecem em cada tela e passam a divergir uma a uma.
 *
 * Playbook § Espaço, raio e sombra: borda + sombra leve, nunca duas superfícies
 * elevadas empilhadas — a de dentro usa `flat`.
 */

type Raio = 'card' | 'panel';

export const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    flat?: boolean;
    raio?: Raio;
    /** Destaque de contexto: borda no azul da ação (use com parcimônia). */
    destacado?: boolean;
    as?: 'div' | 'section' | 'aside' | 'article';
  }
>(({ className, flat = false, raio = 'card', destacado = false, as: Componente = 'div', ...props }, ref) => (
  <Componente
    ref={ref}
    className={cn(
      // `overflow-hidden` para que cabeçalho grudado e tabela não vazem pelo
      // canto arredondado.
      'overflow-hidden border bg-bg-surface text-on-surface',
      raio === 'card' ? 'rounded-card' : 'rounded-panel',
      destacado ? 'border-primary-border' : 'border-border',
      flat ? 'shadow-none' : 'shadow-sm',
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { destacado?: boolean }
>(({ className, destacado = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-5 py-4',
      destacado ? 'border-primary-border bg-primary-wash' : 'border-border',
      className,
    )}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => <h2 ref={ref} className={cn('text-card-title', className)} {...props} />,
);
CardTitle.displayName = 'CardTitle';

/** Rótulo de classificação acima do título — PT Sans Narrow em caixa alta. */
export const CardEyebrow = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => <p ref={ref} className={cn('cmp-eyebrow', className)} {...props} />,
);
CardEyebrow.displayName = 'CardEyebrow';

export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-cell text-on-surface-muted', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { semRespiro?: boolean }
>(({ className, semRespiro = false, ...props }, ref) => (
  <div ref={ref} className={cn(semRespiro ? '' : 'flex flex-col gap-4 p-5', className)} {...props} />
));
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-3 border-t border-border px-5 py-4', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';
